import { Link } from "react-router-dom";
import {
    CheckCircle,
    Sparkle,
    DownloadSimple,
    Lightning,
    Printer as PrinterFill,
    FileXls,
    ImageSquare,
    QuestionMark,
} from "@phosphor-icons/react";
import useSeo from "../hooks/useSeo";

/**
 * Generic SEO landing page used by /id-card/school, /id-card/college, /id-card/employee
 * Each instance gets a distinct H1, intro, feature list and FAQ so Google sees
 * unique pages (avoids duplicate-content penalty).
 */
export default function SeoLanding({
    slug,
    h1,
    intro,
    audience, // e.g. "schools"
    keyword, // primary keyword e.g. "school id card maker"
    samplePdfUrl = "/samples/idcardgen-sample.pdf",
    features = [],
    faqs = [],
    seoTitle,
    seoDescription,
}) {
    useSeo({
        title: seoTitle,
        description: seoDescription,
        canonical: `https://idcardgen.com/id-card/${slug}`,
        keywords: `${keyword}, ${keyword} online, free ${keyword}, bulk ${keyword}, ${keyword} india`,
    });

    // Inject FAQ structured data so Google can show rich results
    const faqJsonLd = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
    };

    return (
        <div data-testid={`seo-landing-${slug}`}>
            <script
                type="application/ld+json"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
            />

            {/* Hero */}
            <section className="relative overflow-hidden border-b border-zinc-200 bg-mesh-1">
                <div className="max-w-6xl mx-auto px-6 pt-16 pb-16 grid lg:grid-cols-5 gap-10 items-center">
                    <div className="lg:col-span-3">
                        <div className="inline-flex items-center gap-2 border border-violet-300 bg-white px-3 py-1 text-xs font-mono uppercase tracking-widest mb-5 shadow-sm">
                            <span className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-violet-600 to-pink-500" />
                            For {audience}
                        </div>
                        <h1 className="font-display font-black text-5xl md:text-6xl tracking-tighter leading-[1.05]">
                            {h1.split("|").map((part, i) =>
                                i % 2 === 1 ? (
                                    <span
                                        key={i}
                                        className="bg-clip-text text-transparent bg-gradient-to-r from-violet-600 via-blue-600 to-pink-600"
                                    >
                                        {part}
                                    </span>
                                ) : (
                                    <span key={i}>{part}</span>
                                ),
                            )}
                        </h1>
                        <p className="text-lg text-zinc-600 mt-5 max-w-2xl leading-relaxed">
                            {intro}
                        </p>
                        <div className="flex flex-wrap gap-3 mt-7">
                            <Link
                                to="/register"
                                className="btn-gradient shadow-glow-violet"
                            >
                                Start free trial
                            </Link>
                            <Link to="/pricing" className="btn-secondary">
                                See pricing
                            </Link>
                            <a
                                href={samplePdfUrl}
                                download
                                className="btn-ghost flex items-center gap-2"
                                onClick={() => {
                                    if (typeof window?.gtag === "function") {
                                        window.gtag("event", "download", {
                                            file_name: "sample_pdf",
                                            page: slug,
                                        });
                                    }
                                }}
                            >
                                <DownloadSimple size={16} weight="bold" />{" "}
                                Download sample PDF
                            </a>
                        </div>
                        <div className="flex items-center gap-2 mt-5 text-xs text-zinc-500 font-mono uppercase tracking-widest">
                            <Sparkle
                                size={12}
                                weight="fill"
                                className="text-amber-500"
                            />
                            Free trial · No credit card · UPI / bank payment
                        </div>
                    </div>
                    <div className="lg:col-span-2">
                        <div className="aspect-[7/5] bg-white shadow-2xl border border-zinc-200 p-4 relative overflow-hidden">
                            <div className="absolute inset-0 bg-gradient-to-br from-violet-100 via-white to-pink-100" />
                            <div className="relative h-full flex flex-col items-center justify-center text-center p-6">
                                <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">
                                    {audience} ID
                                </div>
                                <div className="font-display font-black text-3xl tracking-tighter mt-1">
                                    Sample preview
                                </div>
                                <div className="text-sm text-zinc-600 mt-2 max-w-xs">
                                    Download a print-ready PDF to see exactly
                                    what your school's cards will look like.
                                </div>
                                <a
                                    href={samplePdfUrl}
                                    download
                                    className="btn-primary mt-4 inline-flex items-center gap-2"
                                >
                                    <DownloadSimple size={14} weight="bold" />{" "}
                                    Sample.pdf
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features */}
            <section className="max-w-6xl mx-auto px-6 py-16">
                <div className="text-xs font-mono uppercase tracking-widest text-violet-600 font-bold mb-2">
                    ◆ What you get
                </div>
                <h2 className="font-display font-black text-3xl md:text-4xl tracking-tighter mb-10">
                    Everything you need for {audience} cards.
                </h2>
                <div className="grid md:grid-cols-3 gap-5">
                    {features.map((f, i) => (
                        <div
                            key={i}
                            className="border border-zinc-200 bg-white p-6 hover:-translate-y-1 hover:shadow-lg transition-all"
                        >
                            <div className="w-10 h-10 g-violet text-white flex items-center justify-center mb-3 shadow-md">
                                <CheckCircle size={20} weight="duotone" />
                            </div>
                            <div className="font-display font-bold text-lg">
                                {f.title}
                            </div>
                            <div className="text-sm text-zinc-600 mt-1">
                                {f.body}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* How it works */}
            <section className="bg-zinc-50 border-y border-zinc-200">
                <div className="max-w-6xl mx-auto px-6 py-16">
                    <div className="text-xs font-mono uppercase tracking-widest text-pink-600 font-bold mb-2">
                        ◆ How it works
                    </div>
                    <h2 className="font-display font-black text-3xl md:text-4xl tracking-tighter mb-10">
                        Three steps. Print-ready in minutes.
                    </h2>
                    <div className="grid md:grid-cols-3 gap-6">
                        {[
                            {
                                icon: FileXls,
                                title: "1. Upload Excel",
                                body: "Drop your existing student / staff sheet. Names, photos, classes — all auto-mapped.",
                            },
                            {
                                icon: ImageSquare,
                                title: "2. Pick template",
                                body: "Choose from school / college / corporate styles or design your own with the drag editor.",
                            },
                            {
                                icon: PrinterFill,
                                title: "3. Export & print",
                                body: "Bulk PDF lays out 8 cards per A4 page — ready for any local printer or PVC service.",
                            },
                        ].map((s, i) => (
                            <div
                                key={i}
                                className="bg-white p-6 border border-zinc-200"
                            >
                                <s.icon
                                    size={28}
                                    weight="duotone"
                                    className="text-violet-600"
                                />
                                <div className="font-display font-bold text-xl mt-3">
                                    {s.title}
                                </div>
                                <div className="text-sm text-zinc-600 mt-1">
                                    {s.body}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="max-w-3xl mx-auto px-6 py-16">
                <div className="text-xs font-mono uppercase tracking-widest text-blue-600 font-bold mb-2">
                    ◆ FAQ
                </div>
                <h2 className="font-display font-black text-3xl md:text-4xl tracking-tighter mb-8">
                    Common questions
                </h2>
                <div className="space-y-3">
                    {faqs.map((f, i) => (
                        <details
                            key={i}
                            className="border border-zinc-200 bg-white p-4 group"
                        >
                            <summary className="font-semibold cursor-pointer flex items-center justify-between gap-3">
                                <span>{f.q}</span>
                                <QuestionMark
                                    size={16}
                                    weight="bold"
                                    className="text-violet-500 group-open:rotate-180 transition-transform"
                                />
                            </summary>
                            <div className="text-sm text-zinc-600 mt-3 leading-relaxed">
                                {f.a}
                            </div>
                        </details>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section className="bg-zinc-950 text-white">
                <div className="max-w-4xl mx-auto px-6 py-20 text-center">
                    <Lightning
                        size={32}
                        weight="duotone"
                        className="mx-auto text-amber-400"
                    />
                    <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-3">
                        Try it free for your{" "}
                        <span className="bg-clip-text text-transparent bg-gradient-to-r from-violet-400 via-pink-400 to-amber-400">
                            {audience}
                        </span>
                    </h2>
                    <p className="text-white/70 mt-3 max-w-xl mx-auto">
                        20 free cards, no credit card. Watermarked exports for
                        evaluation — upgrade only when you're ready to print.
                    </p>
                    <div className="flex flex-wrap gap-3 justify-center mt-6">
                        <Link
                            to="/register"
                            className="btn-gradient shadow-glow-violet"
                        >
                            Create free account
                        </Link>
                        <Link to="/pricing" className="btn-secondary">
                            See packages
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
